"use client";

import * as React from "react";
import { useMyUser } from "@/hooks/auth/use-my-user";
import { isCrossHost } from "@/lib/admin-hosts";

export interface SwitchableCustomer {
  appId: string;
  name: string;
  switchHref: string;
  opensNewTab: boolean;
}

// TODO: make this an infinite query and apply the search in the backend
export function useSwitchableCustomers() {
  const { myUser } = useMyUser();

  // Unknown until mount: deciding on the server would mismatch hydration.
  const [location, setLocation] = React.useState<Location | null>(null);
  React.useEffect(() => setLocation(window.location), []);

  return React.useMemo(() => {
    const currentName: string =
      myUser?.current_user?.data?.pylo_app?.data?.pylo_customer?.data?.name ?? "";

    // Only default apps (one per customer)
    const apps: any[] = (myUser?.my_users?.data?.map((u: any) => u?.pylo_app?.data) ?? []).filter(
      (app: any) => !!app?.pylo_customer?.data?.name && app?.is_default !== false,
    );

    const toCustomer = (app: any): SwitchableCustomer => ({
      appId: app.id,
      name: app.pylo_customer.data.name,
      switchHref: `/api/auth/switch?app_id=${app.id}`,
      opensNewTab:
        location !== null && isCrossHost(app.pylo_customer.data.subdomain ?? null, location),
    });

    const currentApp = apps.find((app) => app.pylo_customer.data.name === currentName);
    const others = apps
      .filter((app) => app.pylo_customer.data.name !== currentName)
      .map(toCustomer)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    return {
      currentName,
      current: currentApp ? toCustomer(currentApp) : undefined,
      others,
    };
  }, [myUser, location]);
}
